import { API_BASE_URL } from '~/config';
import { ApiCodeSamples, ApiCodeSamplesConfig } from './';

export function createImageToImageApiCodeSamples(config: ApiCodeSamplesConfig, apiKey = '<API_KEY>'): ApiCodeSamples {
  const url = `${API_BASE_URL}${config.path ?? '/inference/v1/image_generation/'}${config.model.name}/image_to_image`;
  const accept = config.body.accept ?? 'image/jpeg';

  let outputFiletype: string;
  switch (accept) {
    case 'image/jpeg':
      outputFiletype = 'jpg';
      break;
    case 'image/png':
      outputFiletype = 'png';
      break;
    default:
      outputFiletype = 'png';
      break;
  }

  const imagePath = config.body.init_image?.name ?? `path/to/image.${outputFiletype}`;

  const imageName = config.body.prompt.split(' ')[0]
    ? config.body.prompt.split(' ')[0].toLowerCase()
    : '<DEFAULT_IMAGE_NAME>';

  const jsExampleLines: string[] = [];

  jsExampleLines.push(
    `import { File, FormData } from "formdata-node";`,
    `import fs from "fs";`,
    `import fetch from "node-fetch";`,
    ``,
    `(async () => {`,
    `    const initImage = new File(
        [fs.readFileSync("${imagePath}")],
        "${imagePath.split('/').pop()}"
    );`,
    `    // Call the image_to_image API to transform the image`,
    `    const formData = new FormData();`,
    `    // This is for Node.js. If in browser, adjust accordingly.`,
    `    formData.append("init_image", initImage);`,
    `    formData.append("prompt", "${config.body.prompt}");`,
  );

  if (config.body.negative_prompt) {
    jsExampleLines.push(`    formData.append("negative_prompt", "${config.body.negative_prompt}");`);
  }

  if (config.body.init_image_mode === 'STEP_SCHEDULE') {
    jsExampleLines.push(
      `    formData.append("init_image_mode", "STEP_SCHEDULE");`,
      `    formData.append("step_schedule_start", "${config.body.step_schedule_start}");`,
      `    formData.append("step_schedule_end", "${config.body.step_schedule_end}");`,
    );
  } else {
    jsExampleLines.push(
      `    formData.append("init_image_mode", "IMAGE_STRENGTH");`,
      `    formData.append("image_strength", "${config.body.image_strength}");`,
    );
  }

  jsExampleLines.push(
    `    formData.append("cfg_scale", ${config.body.cfg_scale});`,
    /* clip_guidance_preset */
  );

  if (config.body.sampler) {
    jsExampleLines.push(`    formData.append("sampler", ${config.body.sampler});`);
  }

  jsExampleLines.push(
    `    formData.append("seed", ${config.body.seed});`,
    `    formData.append("steps", ${config.body.steps});`,
    `    formData.append("safety_check", ${String(config.body.safety_check)});`,
    /* style_preset */
    `
    const response = await fetch("${url}", {
    method: 'POST',
    headers: {
        'Accept': '${accept}',
        'Authorization': \`Bearer ${apiKey}\`,`,
  );

  jsExampleLines.push(
    `    },
    body: formData,
    });

    // To process the response and get the image:
    const buffer = await response.arrayBuffer();

    fs.writeFile('${imageName}.${outputFiletype}', Buffer.from(buffer), () => console.log('finished downloading!'));
})().catch(console.error);`,
  );

  const jsExample = jsExampleLines.join('\n');

  const curlExampleLines: string[] = [];

  curlExampleLines.push(
    `curl -f -X POST "${url}" \\`,
    `  -H 'Content-Type: multipart/form-data' \\`,
    `  -H 'Accept: ${accept}' \\`,
    `  -H "Authorization: Bearer ${apiKey}" \\`,
  );

  curlExampleLines.push(`  -F 'init_image=@"${imagePath}"' \\`, `  -F 'prompt=${config.body.prompt}' \\`);

  if (config.body.negative_prompt !== '') {
    curlExampleLines.push(`  -F 'negative_prompt=${config.body.negative_prompt}' \\`);
  }

  if (config.body.init_image_mode === 'IMAGE_STRENGTH') {
    curlExampleLines.push(
      `  -F 'init_image_mode=IMAGE_STRENGTH' \\`,
      `  -F 'image_strength=${config.body.image_strength}' \\`,
    );
  } else {
    curlExampleLines.push(
      `  -F 'init_image_mode=STEP_SCHEDULE' \\`,
      `  -F 'step_schedule_start=${config.body.step_schedule_start}' \\`,
      `  -F 'step_schedule_end=${config.body.step_schedule_end}' \\`,
    );
  }

  curlExampleLines.push(
    `  -F 'cfg_scale=${config.body.cfg_scale}' \\`,
    /* clip_guidance_preset */
  );

  if (config.body.sampler && config.body.sampler !== 'null') {
    curlExampleLines.push(`  -F 'sampler=${config.body.sampler}' \\`);
  }

  curlExampleLines.push(
    `  -F 'seed=${config.body.seed}' \\`,
    `  -F 'steps=${config.body.steps}' \\`,
    `  -F 'safety_check=${String(config.body.safety_check)}' \\`,
    /* style_preset */
    `  -o output.${outputFiletype}`,
  );

  const curlExample = curlExampleLines.join('\n');

  function pythonEscapePrompt(prompt: string): string {
    return prompt.replace(/"/gu, '\\"');
  }

  let pyOutputImageFormat: string;
  switch (accept) {
    case 'image/jpeg':
      pyOutputImageFormat = '\n    output_image_format="JPG",';
      break;
    case 'image/png':
      pyOutputImageFormat = '\n    output_image_format="PNG",';
      break;
    default:
      pyOutputImageFormat = '';
      break;
  }

  const negPromptLine =
    config.body.negative_prompt === ''
      ? ''
      : `\n    negative_prompt="${pythonEscapePrompt(config.body.negative_prompt)}",`;
  const pySamplerString = config.body.sampler ? `"${config.body.sampler}"` : 'None';

  let conditioningLines: string[] = [];
  conditioningLines =
    config.body.init_image_mode === 'IMAGE_STRENGTH'
      ? [`    init_image_mode="IMAGE_STRENGTH",`, `    image_strength=${config.body.image_strength},`]
      : [
          `    init_image_mode="STEP_SCHEDULE",`,
          `    step_schedule_start=${config.body.step_schedule_start},`,
          `    step_schedule_end=${config.body.step_schedule_end},`,
        ];

  const conditioningString = conditioningLines.join('\n');

  const pythonExample = `# pip install 'fireworks-ai>=0.7.0'
import fireworks.client
from fireworks.client.image import ImageInference, Answer

# Initialize the ImageInference client
fireworks.client.api_key = "${apiKey}"
inference_client = ImageInference(model="${config.model.id}")

# Modify an existing image using the image_to_image method
answer : Answer = inference_client.image_to_image(
    init_image="${imagePath}",
    prompt="${pythonEscapePrompt(config.body.prompt)}",${negPromptLine}
    cfg_scale=${config.body.cfg_scale},
    sampler=${pySamplerString},
    steps=${config.body.steps},
    seed=${config.body.seed},
    safety_check=${config.body.safety_check ? 'True' : 'False'},${pyOutputImageFormat}
${conditioningString}
    # Add additional parameters here
)

if answer.image is None:
  raise RuntimeError(f"No return image, {answer.finish_reason}")
else:
  answer.image.save("output.${outputFiletype}")`;

  return {
    typescript: jsExample,
    python: pythonExample,
    shell: curlExample,
  };
}
